/*
 * TAO internal quiz engine.
 * Replaces the retired Heyflow runtime. Behavior is a faithful reproduction of the
 * observed flow: linear navigation, single-select auto-advance, multi-select + Continue
 * with required validation, timed loaders, autoplay carousel, UTM/click-id capture,
 * analytics emissions (dataLayer + Meta pixel + GA4 via site gtag) identical to Heyflow
 * (plus a uniform quiz_answer GA4 event for analysis), and the success
 * redirect (per-page target/delay, market + locale handling).
 *
 * Follows repo conventions: document-level delegation, named handlers, data-* hooks,
 * idempotent per-root hydration, no per-element listeners.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-tq-root]';
  var initialized = false;

  /* ---------- utilities ---------- */

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function sanitizeEventName(text) {
    return String(text || '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
  }

  function getState(root) {
    if (!root.__tqState) {
      root.__tqState = { index: 0, screens: qsa(root, '[data-tq-screen]'), answers: {}, redirected: false, timers: [] };
    }
    return root.__tqState;
  }

  function clearTimers(state) {
    state.timers.forEach(function (t) { clearTimeout(t); });
    state.timers = [];
  }

  /* ---------- analytics (Heyflow-compatible) ---------- */

  function dl(root, payload) {
    var cfg = root.__tqConfig || {};
    // base keys exactly as the live Heyflow runtime emits them (FLOW_ID, not
    // HEYFLOW_ID; no GA4 id rides on dataLayer events)
    var base = {
      source: 'heyflow',
      trackingVersion: 100,
      origin: window.location.origin,
      pathname: window.location.pathname.replace(/\/$/, ''),
      FLOW_ID: cfg.flowId || ''
    };
    var out = Object.assign({}, payload, base);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(out);
  }

  function fb(event, custom) {
    if (typeof window.fbq !== 'function') { return; }
    try {
      if (custom) { window.fbq('trackCustom', event, {}); }
      else { window.fbq('track', event, {}); }
    } catch (e) { /* pixel absent — non-fatal */ }
  }

  /* GA4 twin of every pixel emission, using Heyflow's runtime name map
     (PageView -> page_view, CompleteRegistration -> sign_up, customs pass
     through). Custom names are capped at GA4's 40-char event-name limit;
     dataLayer/Meta names stay untruncated to preserve Heyflow byte-parity. */
  function ga(root, event, custom) {
    if (typeof window.gtag !== 'function') { return; }
    var cfg = root.__tqConfig || {};
    var name = custom ? event.slice(0, 40)
      : (event === 'PageView' ? 'page_view' : 'sign_up');
    var params = {};
    if (cfg.ga4) { params.send_to = cfg.ga4; }
    try { window.gtag('event', name, params); } catch (e) { /* non-fatal */ }
  }

  /* Uniform analysis event alongside the parity stream: one quiz_answer per
     answered option with question/answer/quiz params (GA4 caps param values
     at 100 chars; the three params need registering as custom dimensions on
     the property before they appear in reports). */
  function gaQuizAnswer(root, question, answer) {
    if (typeof window.gtag !== 'function') { return; }
    var cfg = root.__tqConfig || {};
    var params = {
      question: String(question || '').slice(0, 100),
      answer: String(answer || '').slice(0, 100),
      quiz: String(cfg.quiz || '').slice(0, 100)
    };
    if (cfg.ga4) { params.send_to = cfg.ga4; }
    try { window.gtag('event', 'quiz_answer', params); } catch (e) { /* non-fatal */ }
  }

  function screenIdOf(screen) {
    return screen.getAttribute('data-tq-screen-id') || screen.getAttribute('data-tq-screen');
  }

  function emitScreenView(root, screen, prev) {
    var payload = {
      screen_id: screenIdOf(screen),
      screen_name: screen.getAttribute('data-tq-screen'),
      event: 'heyflow-screen-view'
    };
    if (prev) {
      payload.previous_screen_id = screenIdOf(prev);
      payload.previous_screen_name = prev.getAttribute('data-tq-screen');
    }
    dl(root, payload);
    fb('PageView', false);
    ga(root, 'PageView', false);
  }

  function emitScreenLeave(root, screen) {
    var name = screen.getAttribute('data-tq-screen');
    var screens = {};
    screens[name] = [];
    dl(root, { screen_name: name, payload: [], screens: screens, event: 'heyflow-screen-leave' });
  }

  function emitAnswer(root, answer) {
    dl(root, {
      event: 'heyflow-input-click',
      custom_event_name: sanitizeEventName(answer),
      custom_event_label: answer
    });
    fb(sanitizeEventName(answer), true);
    ga(root, sanitizeEventName(answer), true);
  }

  /* cumulative answers in Heyflow's field format: one entry per answered
     multiple-choice (document order) plus every query-string param as an
     "additional-" field — matches the live heyflow-change/submit payloads */
  function collectFields(root) {
    var fields = [];
    var simple = {};
    qsa(root, '[data-tq-mc]').forEach(function (mc) {
      var checked = qsa(mc, '.tq-opt-input:checked');
      if (!checked.length) { return; }
      var variable = mc.getAttribute('data-tq-var') || '';
      var labels = checked.map(function (i) { return i.getAttribute('data-tq-answer'); });
      fields.push({
        id: variable,
        label: variable,
        value: labels.join(', '),
        values: checked.map(function (i) {
          return { name: i.getAttribute('data-tq-opt-id') || '', answer: i.getAttribute('data-tq-answer') };
        }),
        variable: variable,
        sensitive: false
      });
      simple[variable] = labels.join(', ');
    });
    var params = new URLSearchParams(window.location.search);
    params.forEach(function (v, k) {
      fields.push({ id: 'additional-' + k, label: k, value: v, values: [{ name: v, answer: v }], variable: k, sensitive: false });
      simple[k] = v;
    });
    return { fields: fields, fieldsSimple: simple };
  }

  function emitChange(root) {
    var f = collectFields(root);
    dl(root, { fields: f.fields, fieldsSimple: f.fieldsSimple, event: 'heyflow-change' });
  }

  function emitButtonClick(root, id, label) {
    dl(root, { custom_event_name: id || 'button', custom_event_label: label, event: 'heyflow-button-click' });
  }

  function emitSubmit(root, state) {
    var f = collectFields(root);
    dl(root, { event: 'heyflow-submit', fields: f.fields, fieldsSimple: f.fieldsSimple });
    fb('CompleteRegistration', false);
    ga(root, 'CompleteRegistration', false);
  }

  /* ---------- Klaviyo (first-party answer capture, anonymous profile) ----------
     Events post directly to Klaviyo's client API with a generated anonymous_id:
     klaviyo.track() drops anonymous events server-side (they wait in localStorage
     for an identify that ad traffic never performs), whereas /client/events with
     an anonymous_id profile records them immediately. When the visitor's email is
     known (logged in), it rides on the same profile, which merges the anonymous
     history onto the email profile. */

  var KLAVIYO_REVISION = '2026-07-15';
  var ANON_KEY = 'tq_anon_id';

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var i = 0; i < 16; i++) { bytes[i] = Math.floor(Math.random() * 256); }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = Array.prototype.map.call(bytes, function (b) { return (b + 0x100).toString(16).slice(1); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  /* stable per-visitor id, persisted in both cookie and localStorage so it
     survives whichever store the browser clears first */
  function getAnonId() {
    var id = null;
    var m = document.cookie.match(/(?:^|;\s*)tq_anon_id=([^;]+)/);
    if (m) { id = m[1]; }
    if (!id) {
      try { id = localStorage.getItem(ANON_KEY); } catch (e) { /* storage blocked */ }
    }
    if (!id) { id = uuid(); }
    try { localStorage.setItem(ANON_KEY, id); } catch (e) { /* storage blocked */ }
    document.cookie = ANON_KEY + '=' + id + '; path=/; max-age=31536000; SameSite=Lax; Secure';
    return id;
  }

  function klBase(root) {
    var cfg = root.__tqConfig || {};
    var base = {
      quiz: cfg.quiz || cfg.flowId || '',
      market: cfg.market || '',
      locale: cfg.locale || ''
    };
    if (cfg.customer) { base.customer_id = cfg.customer; }
    var attr = (getState(root).attribution) || {};
    Object.keys(attr).forEach(function (k) { if (attr[k]) { base[k] = attr[k]; } });
    return base;
  }

  function kl(root, name, props) {
    var cfg = root.__tqConfig || {};
    var payload = Object.assign(klBase(root), props || {});
    if (!cfg.klaviyoCompany) {
      // no company id configured (e.g. QA harness): fall back to the onsite
      // tracker, which only records for Klaviyo-identified visitors
      try {
        if (window.klaviyo && typeof window.klaviyo.track === 'function') {
          window.klaviyo.track(name, payload);
        } else {
          window._learnq = window._learnq || [];
          window._learnq.push(['track', name, payload]);
        }
      } catch (e) { /* Klaviyo absent — non-fatal */ }
      return;
    }

    var profile = { anonymous_id: getAnonId() };
    if (cfg.email) { profile.email = cfg.email; }
    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: payload,
          time: new Date().toISOString(),
          unique_id: uuid(),
          metric: { data: { type: 'metric', attributes: { name: name } } },
          profile: { data: { type: 'profile', attributes: profile } }
        }
      }
    };
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.klaviyoCompany), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(body),
        keepalive: true // survives the success-screen redirect
      }).catch(function () { /* network failure — non-fatal */ });
    } catch (e) { /* fetch absent — non-fatal */ }
  }

  /* readable question text for a screen: the heading of the last rich-text block
     before the choices (handles the start screen, where a promo H2 precedes the
     actual question H3), ignoring the H4 "choose as many" hint. */
  function questionText(screen) {
    var mc = screen.querySelector('[data-tq-mc]');
    var block = null;
    if (mc) {
      qsa(screen, '.tq-rich').forEach(function (r) {
        if (r.compareDocumentPosition(mc) & Node.DOCUMENT_POSITION_FOLLOWING) { block = r; }
      });
    }
    var scope = block || screen;
    var h = scope.querySelector('h1, h2, h3');
    return h ? h.textContent.trim() : screen.getAttribute('data-tq-screen');
  }

  /* full {question: answer} map across all answered screens, for the completion record */
  function collectAnswers(root) {
    var out = {};
    getState(root).screens.forEach(function (screen) {
      var checked = qsa(screen, '.tq-opt-input:checked').map(function (i) { return i.getAttribute('data-tq-answer'); });
      if (checked.length) { out[questionText(screen)] = checked.length > 1 ? checked : checked[0]; }
    });
    return out;
  }

  /* the same answers as flat top-level answer_<question> props — Klaviyo
     segments and flow triggers can only filter on flat event properties */
  function flattenAnswers(answers) {
    var out = {};
    Object.keys(answers).forEach(function (q) {
      var key = 'answer_' + sanitizeEventName(q).toLowerCase().slice(0, 100);
      out[key] = answers[q];
    });
    return out;
  }

  /* stamp the visitor id + answers onto the cart so they surface on the
     Placed Order event's note_attributes — the buyer-side join key. Single
     leading underscore (double breaks Shopify's attribute handling); the
     offer-engine cart rebuild preserves foreign attributes. */
  function stampCartAttributes(root, answers) {
    var state = getState(root);
    if (state.cartStamped) { return; }
    state.cartStamped = true;
    var cfg = root.__tqConfig || {};
    var attributes = {
      _quiz_anonymous_id: getAnonId(),
      _quiz: cfg.quiz || cfg.flowId || '',
      _quiz_answers: JSON.stringify(answers)
    };
    var url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: true // survives the success-screen redirect
      }).catch(function () { /* network failure — non-fatal */ });
    } catch (e) { /* fetch absent — non-fatal */ }
  }

  /* ---------- navigation ---------- */

  function updateProgress(root, state) {
    var fill = qs(root, '[data-tq-progress-fill]');
    if (!fill) { return; }
    // Heyflow divides by every screen in the flow's nav order, which on
    // California flows includes the unreachable email screen (22 vs our 21
    // rendered) — the section passes the flow's true total.
    var cfg = root.__tqConfig || {};
    var total = cfg.progressTotal || state.screens.length;
    var pct = total > 0 ? ((state.index + 1) / total) * 100 : 0;
    fill.style.width = pct + '%';
  }

  function showScreen(root, index) {
    var state = getState(root);
    if (index < 0 || index >= state.screens.length) { return; }
    clearTimers(state);

    var prev = state.screens[state.index];
    var next = state.screens[index];
    if (prev && prev !== next) { emitScreenLeave(root, prev); }

    state.screens.forEach(function (s) {
      s.removeAttribute('data-tq-active');
      s.setAttribute('aria-hidden', 'true');
    });
    next.setAttribute('data-tq-active', '');
    next.setAttribute('aria-hidden', 'false');
    state.index = index;

    updateProgress(root, state);
    try { root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) { root.scrollIntoView(); }

    emitScreenView(root, next, prev);
    activateScreen(root, next);
  }

  function goNext(root) {
    var state = getState(root);
    if (state.index + 1 < state.screens.length) { showScreen(root, state.index + 1); }
  }

  /* screen-type activation (loaders, carousels, success redirect) */
  function activateScreen(root, screen) {
    var state = getState(root);

    if (screen.getAttribute('data-tq-screen') === 'success') {
      emitSubmit(root, state);
      var answers = collectAnswers(root);
      kl(root, 'Completed Quiz', Object.assign({ answers: answers }, flattenAnswers(answers)));
      stampCartAttributes(root, answers);
      triggerRedirect(root);
      startLoader(root, screen); // still animate the spinner during the delay
      return;
    }

    var loader = qs(screen, '[data-tq-loader]');
    if (loader) { startLoader(root, screen); }

    var carousel = qs(screen, '[data-tq-carousel]');
    if (carousel) { startCarousel(root, carousel); }
  }

  function startLoader(root, screen) {
    var state = getState(root);
    var loader = qs(screen, '[data-tq-loader]');
    if (!loader) { return; }
    if (screen.getAttribute('data-tq-screen') === 'success') { return; } // success spins until redirect
    var spinner = qs(loader, '.tq-spinner');
    var timeout = parseInt(loader.getAttribute('data-tq-timeout'), 10) || 4100;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    state.timers.push(setTimeout(function () {
      // Heyflow ends each loader by filling the ring and drawing a checkmark
      // (0.8s), then advances at ~1s after the timeout (measured on live walks:
      // every loader dwell = timeout + ~1000ms).
      if (spinner && !reduce) {
        spinner.setAttribute('data-tq-complete', '');
        state.timers.push(setTimeout(function () { goNext(root); }, 1000));
      } else {
        goNext(root);
      }
    }, timeout));
  }

  function startCarousel(root, carousel) {
    if (carousel.__tqCarInit) { return; } // idempotent: don't double-bind controls
    carousel.__tqCarInit = true;
    var state = getState(root);
    var track = qs(carousel, '[data-tq-track]');
    var dots = qsa(carousel, '.tq-dot');
    if (!track || !track.children.length) { return; }
    // Heyflow's swiper config (read from the live instance): autoplay delay
    // 1300ms counted from transition end, speed 800ms, loop true,
    // disableOnInteraction true.
    var delay = parseInt(carousel.getAttribute('data-tq-delay'), 10) || 1300;
    var SPEED = 800;
    var count = track.children.length;
    var i = 0;
    var autoplay = true;
    var animating = false;

    // seamless loop: a clone of the last slide leads and a clone of the first
    // trails, so both wrap directions glide forward/backward without rewinding
    var leadClone = track.children[count - 1].cloneNode(true);
    var tailClone = track.children[0].cloneNode(true);
    leadClone.setAttribute('aria-hidden', 'true');
    tailClone.setAttribute('aria-hidden', 'true');
    track.insertBefore(leadClone, track.children[0]);
    track.appendChild(tailClone);
    var slides = qsa(track, '.tq-slide');
    function slideLeft(n) { return slides[n + 1].offsetLeft; } // +1 skips the lead clone

    // park on the real first slide (snap re-engages for manual swipes; it is
    // lifted while the engine glides or teleports across the clones)
    track.style.scrollSnapType = 'none';
    track.scrollLeft = slideLeft(0);
    track.style.scrollSnapType = 'x mandatory';

    function setDot(n) { dots.forEach(function (d, di) { if (di === n) { d.setAttribute('data-tq-dot-active', ''); } else { d.removeAttribute('data-tq-dot-active'); } }); }

    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
    function animateTo(left, done) {
      animating = true;
      track.style.scrollSnapType = 'none';
      var from = track.scrollLeft;
      var t0 = performance.now();
      function frame(now) {
        var t = Math.min(1, (now - t0) / SPEED);
        track.scrollLeft = from + (left - from) * easeOutCubic(t);
        if (t < 1) { requestAnimationFrame(frame); } else { if (done) { done(); } track.style.scrollSnapType = 'x mandatory'; animating = false; }
      }
      requestAnimationFrame(frame);
    }

    /* n may be -1 (backward wrap) or count (forward wrap) */
    function goTo(n) {
      if (animating) { return; }
      setDot(((n % count) + count) % count);
      animateTo(slideLeft(n), function () {
        if (n >= count) { track.scrollLeft = slideLeft(0); i = 0; }
        else if (n < 0) { track.scrollLeft = slideLeft(count - 1); i = count - 1; }
        else { i = n; }
        schedule();
      });
    }
    function stopAutoplay() { autoplay = false; }
    function schedule() {
      if (!autoplay) { return; }
      state.timers.push(setTimeout(function () { if (autoplay && !animating) { goTo(i + 1); } }, delay));
    }
    setDot(0);

    // manual controls, matching Heyflow's swiper: the 44px prev/next hit areas
    // are invisible but clickable; dots are clickable; any interaction stops
    // autoplay permanently (disableOnInteraction)
    var prev = qs(carousel, '[data-tq-carousel-prev]');
    var next = qs(carousel, '[data-tq-carousel-next]');
    if (prev) { prev.addEventListener('click', function () { stopAutoplay(); goTo(i - 1); }); }
    if (next) { next.addEventListener('click', function () { stopAutoplay(); goTo(i + 1); }); }
    dots.forEach(function (d, di) { d.addEventListener('click', function () { stopAutoplay(); goTo(di); }); });
    track.addEventListener('pointerdown', stopAutoplay, { once: true });
    track.addEventListener('scroll', function () {
      if (animating) { return; }
      // manual swipe: track the nearest real slide, teleporting off the clones
      var pitch = slides[2].offsetLeft - slides[1].offsetLeft;
      var idx = Math.round((track.scrollLeft - slideLeft(0)) / pitch);
      if (idx < 0) { track.scrollLeft = slideLeft(count - 1); idx = count - 1; }
      else if (idx >= count) { track.scrollLeft = slideLeft(0); idx = 0; }
      if (idx !== i) { i = idx; setDot(i); }
    }, { passive: true });

    if (count > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      schedule();
    }
  }

  /* ---------- redirect (per-page target/delay, market + locale) ---------- */

  function triggerRedirect(root) {
    var state = getState(root);
    var cfg = root.__tqConfig || {};
    if (state.redirected) { return; }
    state.redirected = true;
    var delay = typeof cfg.redirectDelay === 'number' ? cfg.redirectDelay : 3500;
    var target = cfg.redirectUrl;
    if (!target) { return; }

    // Liquid supplies the localized destination, including any explicit market override.
    state.timers.push(setTimeout(function () { window.location.replace(target); }, delay));
  }

  /* ---------- answer handling ---------- */

  function recordAnswer(root, screen) {
    var state = getState(root);
    var name = screen.getAttribute('data-tq-screen');
    var checked = qsa(screen, '.tq-opt-input:checked').map(function (i) { return i.getAttribute('data-tq-answer'); });
    if (checked.length) { state.answers[name] = checked.length > 1 ? checked : checked[0]; }
    return checked;
  }

  function handleOptionChange(root, input) {
    var screen = input.closest('[data-tq-screen]');
    var mc = input.closest('[data-tq-mc]');
    if (!screen || !mc) { return; }

    // A click queued while the engine was still loading, or a second rapid tap
    // on a single-select screen, can be delivered after the quiz has already
    // advanced. Screens are hidden rather than removed, so that click would
    // answer a screen the visitor has left and schedule a second advance,
    // skipping the next screen permanently. Only the screen on show answers.
    var state = getState(root);
    if (state.screens.length && screen !== state.screens[state.index]) { return; }

    mc.removeAttribute('data-tq-error');
    var answer = input.getAttribute('data-tq-answer');
    if (input.checked) {
      emitAnswer(root, answer);
      gaQuizAnswer(root, questionText(screen), answer);
      kl(root, 'Answered Quiz Question', { question: questionText(screen), answer: answer });
    }
    recordAnswer(root, screen);
    emitChange(root);

    // single-select auto-advance — Heyflow advances immediately (~20ms measured
    // on live walks); one macrotask lets the checked state paint first
    if (mc.getAttribute('data-tq-auto') === 'true' && mc.getAttribute('data-tq-multi') !== 'true') {
      state.timers.push(setTimeout(function () { goNext(root); }, 0));
    }
  }

  function handleContinue(root, btn) {
    var screen = btn.closest('[data-tq-screen]');
    if (!screen) { return; }
    emitButtonClick(root, btn.getAttribute('data-tq-button-id'), 'Continue');
    var mc = qs(screen, '[data-tq-mc]');
    if (mc && mc.getAttribute('data-tq-required') === 'true') {
      var checked = qsa(screen, '.tq-opt-input:checked');
      if (!checked.length) { mc.setAttribute('data-tq-error', ''); return; }
    }
    recordAnswer(root, screen);
    goNext(root);
  }

  /* ---------- query-string capture (PASS_QS) ---------- */

  function captureQueryString(root) {
    var params = new URLSearchParams(window.location.search);
    qsa(root, '[data-tq-capture]').forEach(function (input) {
      var key = input.getAttribute('data-tq-capture');
      if (params.has(key)) { input.value = params.get(key); }
    });
  }

  /* ad-attribution params carried into every Klaviyo event */
  function readAttribution() {
    var params = new URLSearchParams(window.location.search);
    var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'tw_source', 'tw_adid'];
    var out = {};
    keys.forEach(function (k) { if (params.has(k)) { out[k] = params.get(k); } });
    return out;
  }

  /* ---------- hydration ----------
     Split in two so the quiz answers taps from the moment its own script runs
     rather than from DOMContentLoaded. DCL waits on storefront scripts loaded
     after this one, which left the first screen visible but deaf; a tap in that
     gap checked the radio natively and was then unrecoverable, because tapping
     an already-checked radio fires no change event.

     wireSection  — everything that makes the quiz work. Runs immediately.
     commitSection — the two pixel PageViews, still on DOMContentLoaded.

     heyflow-init and the Klaviyo 'Started Quiz' move into wireSection: both are
     transport-independent (dataLayer is a self-creating array, Klaviyo posts
     directly), so emitting them alongside the wiring keeps them ahead of any
     answer the visitor can produce without needing an event queue. The two
     pixel calls stay behind because they depend on globals that do not exist
     this early. */

  function wireSection(root) {
    if (!root || root.__tqWired) { return; }
    root.__tqWired = true;

    try {
      wireSectionBody(root);
    } catch (e) {
      // let the DOMContentLoaded pass try again rather than stranding the root
      root.__tqWired = false;
    }
  }

  function wireSectionBody(root) {
    root.__tqConfig = {
      flowId: root.getAttribute('data-tq-flow-id') || '',
      ga4: root.getAttribute('data-tq-ga4') || '',
      redirectUrl: root.getAttribute('data-tq-redirect-url') || '',
      redirectDelay: parseInt(root.getAttribute('data-tq-redirect-delay'), 10) || 3500,
      progressTotal: parseInt(root.getAttribute('data-tq-progress-total'), 10) || 0,
      quiz: root.getAttribute('data-tq-quiz') || '',
      market: root.getAttribute('data-tq-market') || '',
      locale: root.getAttribute('data-tq-locale') || '',
      customer: root.getAttribute('data-tq-customer') || '',
      klaviyoCompany: root.getAttribute('data-tq-klaviyo') || '',
      email: root.getAttribute('data-tq-email') || ''
    };

    var state = getState(root);
    state.screens = qsa(root, '[data-tq-screen]');
    state.attribution = readAttribution();
    captureQueryString(root);

    // Trust the rendered active screen over getState's default of 0, so the
    // engine can never operate on a screen the visitor cannot see.
    var active = qs(root, '[data-tq-screen][data-tq-active]');
    var activeIndex = state.screens.indexOf(active);
    state.index = activeIndex > -1 ? activeIndex : 0;

    updateProgress(root, state);
    clearRestoredAnswers(root, state);

    // Heyflow emits exactly one dataLayer event at load (heyflow-init) and no
    // screen-view for the first screen.
    dl(root, { fields: [], screens: {}, event: 'heyflow-init' });
    kl(root, 'Started Quiz', {});

    // Activating the success screen submits, stamps the cart and starts the
    // redirect, so it belongs with the load events rather than the wiring. It
    // is never the first screen on the live templates; this is a guard only.
    var first = state.screens[state.index];
    if (first && first.getAttribute('data-tq-screen') !== 'success') {
      activateScreen(root, first);
    }
  }

  /* Browsers restore checked radios and checkboxes across reload and
     back/forward navigation — verified live: a back navigation re-ran hydration
     with the first screen's radio already checked. collectAnswers walks every
     screen, so a check restored from an earlier session would be reported as an
     answer to this one and would reach _quiz_answers. Keep only what the
     visitor can currently see, and only on a fresh navigation. */
  function clearRestoredAnswers(root, state) {
    var nav = null;
    try { nav = performance.getEntriesByType('navigation')[0] || null; } catch (e) { nav = null; }
    var freshNav = !nav || nav.type === 'navigate';
    var current = state.screens[state.index];

    qsa(root, '.tq-opt-input').forEach(function (input) {
      if (!input.checked) { return; }
      if (!freshNav || input.closest('[data-tq-screen]') !== current) { input.checked = false; }
    });
  }

  function commitSection(root) {
    if (!root) { return; }
    wireSection(root);
    if (root.__tqCommitted || !root.__tqWired) { return; }
    root.__tqCommitted = true;

    // Heyflow's pixel fires one PageView at load.
    fb('PageView', false);
    ga(root, 'PageView', false);

    var state = getState(root);
    var first = state.screens[state.index];
    if (first && first.getAttribute('data-tq-screen') === 'success') {
      activateScreen(root, first);
    }
  }

  /* ---------- global delegation (init once) ---------- */

  function initOnce() {
    if (initialized) { return; }
    initialized = true;

    document.addEventListener('change', function (e) {
      var input = e.target.closest('.tq-opt-input');
      if (!input) { return; }
      var root = input.closest(ROOT_SELECTOR);
      if (root) { handleOptionChange(root, input); }
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-tq-action]');
      if (btn) {
        var root = btn.closest(ROOT_SELECTOR);
        if (root) { handleContinue(root, btn); }
        return;
      }
      // skip links carry a real href — let them navigate, but record the click
      // the way Heyflow does (it's a link-styled button block)
      var skip = e.target.closest('[data-tq-skip]');
      if (skip) {
        var sroot = skip.closest(ROOT_SELECTOR);
        if (sroot) { emitButtonClick(sroot, skip.getAttribute('data-tq-button-id'), skip.textContent.trim()); }
      }
    });
  }

  function wireAll() {
    initOnce();
    qsa(document, ROOT_SELECTOR).forEach(wireSection);
  }

  function boot() {
    initOnce();
    qsa(document, ROOT_SELECTOR).forEach(commitSection);
  }

  // Register the later passes before wiring, so a failure while wiring cannot
  // leave the load events unregistered.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  }

  // Theme editor: re-hydrate on section load
  document.addEventListener('shopify:section:load', boot);

  // This script is parser-blocking and sits after the whole section, so every
  // screen is already in the DOM: wire now rather than waiting for DCL.
  wireAll();

  if (document.readyState !== 'loading') { boot(); }
})();
